// The Projects list's filter functions. The shared panel's selection is first cut down to what this list
// can honour (custom fields available on it; Win Probability only where it is offered), then the shared
// project filter runs with the custom field context it needs passed in as options. Win Probability always
// matches against the fixed default ranges, so no stage data is needed here.
import type {Moment} from 'moment';
import {FEATURE_FLAGS, FILTER_TYPE} from '../../../../constants';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import CompanySetupUtil from '../../../shared/util/CompanySetupUtil';
import {isClientUser} from '../../../shared/util/PermissionsUtil';
import {getEntityOperatorKey, getFilterFunctions} from '../../../shared/components/filters/filter_logic';
import type {CustomFieldColumnDef} from '../../../shared/components/custom-field-columns/customFieldColumnModel';
import {sanitizeCustomFieldSelection} from '../../../shared/components/custom-field-filters/customFieldFilterSelection';
import {projectCustomFieldValues} from './projectCustomFieldColumns';

/** Win Probability on this list follows the Win Chance column's access, behind the list filters flag. */
export const shouldOfferProjectWinProbabilityFilter = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.CUSTOM_FIELDS_LISTS) && CompanySetupUtil.hasFinance() && !isClientUser();

export interface ProjectListFilterContext {
	customFieldDefinitions: CustomFieldColumnDef[];
	winProbabilityEnabled: boolean;
	/** A fixed day for date presets. Without one, presets resolve against the current day each time the list filters. */
	today?: Moment;
}

type FilterObject = {project?: Record<string, unknown>} | null | undefined;

export function resolveProjectListFilters(
	filters: FilterObject,
	ctx: ProjectListFilterContext
): {project: Record<string, unknown>} {
	const project = sanitizeCustomFieldSelection(filters?.project, ctx.customFieldDefinitions);
	if (!ctx.winProbabilityEnabled) {
		delete project[FILTER_TYPE.OPPORTUNITY_WIN_PROBABILITY];
		delete project[getEntityOperatorKey(FILTER_TYPE.OPPORTUNITY_WIN_PROBABILITY)];
	}
	return {...filters, project};
}

export function buildProjectListFilterFunctions(filters: FilterObject, ctx: ProjectListFilterContext) {
	const base = getFilterFunctions(resolveProjectListFilters(filters, ctx));
	const customFields = {
		definitions: ctx.customFieldDefinitions,
		valuesOf: projectCustomFieldValues,
		today: ctx.today,
	};
	return {
		...base,
		projectFilter: (project: unknown, options?: Record<string, unknown>) =>
			base.projectFilter(project, {...options, customFields}),
	};
}
